import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Buildings, Globe, Star, TrendUp, ShieldCheck, ChartPieSlice } from "@phosphor-icons/react";
import { GlassCard } from "@/components/glass/GlassCard";
import { PageShell } from "@/components/layout/AppLayout";
import api from "@/lib/api";
import SearchSortBar from "@/components/SearchSortBar";
import useSearchSort from "@/hooks/useSearchSort";

function money(v, ccy = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy, maximumFractionDigits: 0 }).format(v || 0);
}

export default function Counterparty() {
  const [counterparties, setCounterparties] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/counterparties").then(({ data }) => { setCounterparties(data); setActive(data[0] || null); });
  }, []);

  const s = useSearchSort(counterparties, {
    searchKeys: ["name", "industry", "country"],
    sortOptions: [
      { value: "name", label: "Name A→Z", defaultDir: "asc" },
      { value: "risk_rating", label: "Risk", defaultDir: "asc" },
      { value: "active_contracts", label: "# contracts", defaultDir: "desc" },
      { value: "total_contract_value", label: "Total value", defaultDir: "desc" },
      { value: "financial_health", label: "Financial health", defaultDir: "desc" },
      { value: "relationship_score", label: "Relationship", defaultDir: "desc" },
    ],
    defaultSort: "total_contract_value",
  });

  return (
    <PageShell title="Counterparty 360°" subtitle="Full intelligence on every party you contract with">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 space-y-2">
          <SearchSortBar controller={s} placeholder="Search counterparties…" testIdPrefix="cp" />
          {s.items.map((cp) => (
            <button
              key={cp.id}
              onClick={() => setActive(cp)}
              data-testid={`cp-item-${cp.id}`}
              className={`w-full text-left p-4 rounded-lg transition border ${
                active?.id === cp.id
                  ? "bg-[var(--surface-strong)] border-[var(--border-strong)]"
                  : "bg-[var(--surface)] border-[var(--border)] hover:border-[var(--border-strong)]"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-[var(--surface-2)] border border-[var(--border)] flex items-center justify-center shrink-0">
                  <Buildings size={16} weight="duotone" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{cp.name}</div>
                  <div className="text-xs text-[var(--text-3)] truncate">{cp.industry}</div>
                </div>
                <span className={`chip chip-${cp.risk_rating}`}>{cp.risk_rating}</span>
              </div>
            </button>
          ))}
        </div>

        {active && (
          <motion.div key={active.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-8 space-y-6">
            <GlassCard strong className="p-8">
              <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
                <div>
                  <h2 className="text-3xl font-medium tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>{active.name}</h2>
                  <div className="text-sm text-[var(--text-2)] mt-1 flex items-center gap-3 flex-wrap">
                    <span className="flex items-center gap-1"><Globe size={12} /> {active.country}</span>
                    <span>{active.industry}</span>
                    {active.website && <a href={active.website} target="_blank" rel="noreferrer" className="text-[var(--accent)] hover:underline">{active.website.replace(/^https?:\/\//, "")}</a>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`chip chip-${active.risk_rating}`}>Risk: {active.risk_rating}</span>
                  <span className={`chip chip-${active.sanctions_check === "clear" ? "low" : "critical"}`}>Sanctions: {active.sanctions_check}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-md bg-[var(--surface)] border border-[var(--border)]">
                  <div className="overline mb-1 flex items-center gap-1"><ChartPieSlice size={10} /> Financial health</div>
                  <div className="text-3xl font-semibold tracking-tighter" style={{ fontFamily: 'var(--font-heading)' }}>{active.financial_health}</div>
                  <div className="text-xs text-[var(--text-3)]">/100</div>
                </div>
                <div className="p-4 rounded-md bg-[var(--surface)] border border-[var(--border)]">
                  <div className="overline mb-1 flex items-center gap-1"><Star size={10} weight="fill" /> Relationship</div>
                  <div className="text-3xl font-semibold tracking-tighter" style={{ fontFamily: 'var(--font-heading)' }}>{active.relationship_score}</div>
                  <div className="text-xs text-[var(--text-3)]">/100</div>
                </div>
                <div className="p-4 rounded-md bg-[var(--surface)] border border-[var(--border)]">
                  <div className="overline mb-1">Active contracts</div>
                  <div className="text-3xl font-semibold tracking-tighter font-mono" style={{ fontFamily: 'var(--font-heading)' }}>{active.active_contracts}</div>
                </div>
                <div className="p-4 rounded-md bg-[var(--surface)] border border-[var(--border)]">
                  <div className="overline mb-1">Total value</div>
                  <div className="text-xl font-semibold tracking-tight font-mono" style={{ fontFamily: 'var(--font-heading)' }}>{money(active.total_contract_value)}</div>
                </div>
              </div>

              {active.notes && (
                <div className="mt-6 p-4 rounded-md bg-[var(--surface)] border border-[var(--border)]">
                  <div className="overline mb-2 flex items-center gap-1"><ShieldCheck size={10} /> Intelligence notes</div>
                  <div className="text-sm text-[var(--text-2)] leading-relaxed">{active.notes}</div>
                </div>
              )}
            </GlassCard>
          </motion.div>
        )}
      </div>
    </PageShell>
  );
}
